import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, X, Ship, Star, MapPin, Loader2, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Option C Book-Now dialog.
 *
 * – For Stay22-eligible cards: renders a live-availability list. Clicking a
 *   stay fires POST /booking/click → opens the wrapped Stay22 URL.
 * – For legacy cards (cruises): renders a single "Book" CTA that
 *   fires POST /booking/click → opens the wrapped URL.
 *
 * No bucket chooser (pleasure / business) — that classification is deferred
 * to the "Did you book this?" pending-confirmation chip on the Booked Trips
 * page (Option C flow).
 */
const TIDS = {
  root: "book-now-dialog",
  close: "book-now-close",
  start: "book-now-start-date",
  end: "book-now-end-date",
  staysList: "book-now-stays-list",
  stayItem: "book-now-stay-item",
  staysLoading: "book-now-stays-loading",
  staysEmpty: "book-now-stays-empty",
  staysError: "book-now-stays-error",
};

const inp =
  "w-full px-3 py-2 rounded-lg bg-tb-cream-2 border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm";

export default function BookNowDialog({ destination, onClose }) {
  const d = destination;
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [noAvailability, setNoAvailability] = useState(false);

  // Normalize fields: support both legacy destination shape & StandardCard shape
  const name = d.name || "";
  const country = d.country || d.location?.country || "";
  const image = d.image || d.images?.[0] || "";
  const priceUsd = d.price_usd ?? d.price_from ?? 0;
  const affiliateUrl = d.affiliate_url || d.booking_url || "";
  const savings = d.metadata?.original_price && d.price_from
    ? Math.round(d.metadata.original_price - d.price_from) : 0;
  const currSymbol = d.currency === "EUR" ? "€" : "$";

  // TravelBio destinations carry `metadata.card_id` — these are the cards
  // that Stay22 can price on demand. For legacy cards (cruises)
  // we skip the Stay22 lookup and use the single affiliate_url as before.
  const cardId = d.metadata?.card_id || null;
  const cardType = d.metadata?.type || null;
  // Bug R1: previously restricted to hotel/resort/destination — but production
  // has attractions (Arethusa Falls, Bridal Veil Falls, Disney) with valid
  // stay22_property_ids in booking_metadata. Restricting by type hides the
  // Stay22 lookup for those cards and shows "Booking not available".
  // Fix: any card that has a card_id (TravelBio-catalogue card) is eligible —
  // the Stay22 ladder handles the actual matching (property_id → address →
  // lat/lng → city+country). User-submits without a property_id still work
  // via the address/lat-lng rungs.
  const stay22PropertyId = d.metadata?.booking_metadata?.stay22_property_id || null;
  const stay22Eligible = Boolean(cardId) &&
    (Boolean(stay22PropertyId) || ["hotel", "resort", "destination", "attraction", "ski_resort", "camping"].includes(cardType));

  const [stays, setStays] = useState([]);
  const [staysLoading, setStaysLoading] = useState(false);
  const [staysError, setStaysError] = useState(null);

  // Fetch stays whenever the card OR the dates change.
  useEffect(() => {
    if (!stay22Eligible) return;
    let cancelled = false;
    setStaysLoading(true);
    setStaysError(null);
    setNoAvailability(false);
    api
      .post("/booking/search", {
        card_id: cardId,
        checkin: startDate || null,
        checkout: endDate || null,
        adults: 2,
        children: 0,
      })
      .then((res) => {
        if (cancelled) return;
        setStays(Array.isArray(res.data) ? res.data : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setStaysError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load live availability");
        setStays([]);
      })
      .finally(() => {
        if (!cancelled) setStaysLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [stay22Eligible, cardId, startDate, endDate]);

  /**
   * Fire POST /booking/click and open the wrapped URL.
   * Works for both Stay22 list items and legacy single-CTA cards.
   */
  const bookStay = async (stay) => {
    // Bug 41 (P0): the anchor-click-after-await approach shipped in
    // OTA17 stopped working reliably in current Chrome/Safari because
    // the `await api.post(...)` below consumes the user-activation
    // transient state — by the time we call `a.click()`, the browser
    // no longer treats it as a user gesture and silently blocks the
    // pop-up (no toast, no console error, no new tab).
    //
    // Fix: open a blank tab SYNCHRONOUSLY at the top of the handler
    // (while user activation is still hot), then navigate that tab
    // to the wrapped URL once the /booking/click round-trip finishes.
    // If the sync-open is blocked (e.g. aggressive popup blocker
    // returns null), we fall back to the anchor-click bypass which
    // still works for a subset of browsers.
    //
    // NOTE: we deliberately do NOT pass `noopener,noreferrer` here —
    // those flags cause `window.open` to return `null` in Chrome, which
    // would defeat the whole point of pre-opening a tab we can navigate
    // later. Instead we null out `preOpenedWin.opener` right BEFORE
    // assigning `location.href` — same security outcome, but we keep
    // the reference we need.
    const preOpenedWin = (() => {
      try {
        return window.open("about:blank", "_blank");
      } catch { return null; }
    })();
    setBusy(true);
    setNoAvailability(false);
    try {
      const { data } = await api.post("/booking/click", {
        hotel_id: stay?.stay22_property_id || d.id,
        hotel_name: stay?.name || name,
        destination_id: d.id,
        stay22_url: stay?.booking_url || affiliateUrl || null,
        check_in: startDate || null,
        check_out: endDate || null,
      });
      let target = null;
      if (data?.wrapped_url) {
        target = data.wrapped_url;
      } else if (stay?.booking_url || affiliateUrl) {
        const url = stay?.booking_url || affiliateUrl;
        target = url.startsWith("http")
          ? url
          : `${process.env.REACT_APP_BACKEND_URL}${url}`;
      }
      // Bug 7 fix retained: hard-validate the outbound URL.
      let validTarget = null;
      try {
        if (target) {
          const parsed = new URL(target);
          if (parsed.protocol === "http:" || parsed.protocol === "https:") {
            validTarget = parsed.toString();
          }
        }
      } catch { validTarget = null; }
      if (validTarget) {
        // Primary path: navigate the pre-opened tab (works even after
        // the await because we OWN that window handle).
        if (preOpenedWin && !preOpenedWin.closed) {
          try {
            // Sever opener BEFORE navigation — same security posture as
            // `rel=noopener` on an anchor, but doesn't force the tab
            // reference to be null.
            try { preOpenedWin.opener = null; } catch { /* cross-origin, noop */ }
            preOpenedWin.location.href = validTarget;
          } catch {
            // Cross-origin write failed — fall through to anchor-click.
            preOpenedWin.close();
            _anchorClickFallback(validTarget);
          }
        } else {
          _anchorClickFallback(validTarget);
        }
      } else {
        if (preOpenedWin && !preOpenedWin.closed) preOpenedWin.close();
        toast.error("Booking link is unavailable right now — please try again in a moment.");
      }
      onClose?.();
    } catch (e) {
      // Clean up the pre-opened tab on error so the user isn't left
      // staring at a blank about:blank.
      if (preOpenedWin && !preOpenedWin.closed) {
        try { preOpenedWin.close(); } catch { /* noop */ }
      }
      const detail = e.response?.data?.detail;
      const isNoAvail =
        e.response?.status === 404 &&
        (detail === "no_availability" ||
          detail?.error === "no_availability");
      if (isNoAvail) {
        setNoAvailability(true);
      } else {
        toast.error(
          formatApiErrorDetail(detail) || "Couldn't complete the booking click",
        );
      }
    } finally {
      setBusy(false);
    }
  };

  // Anchor-click fallback for browsers that block window.open even on
  // a user gesture. Kept as a secondary path so aggressive popup
  // blockers still get a chance to work.
  const _anchorClickFallback = (validTarget) => {
    try {
      const a = document.createElement("a");
      a.href = validTarget;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try { document.body.removeChild(a); } catch { /* noop */ }
      }, 100);
    } catch {
      toast.error("Your browser blocked the booking tab. Please allow pop-ups for TravelBio.");
    }
  };

  return (
    <div
      data-testid={TIDS.root}
      className="fixed inset-0 z-[60] bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-3 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-tb-cream rounded-2xl border-2 border-tb-gold max-w-md w-full overflow-hidden shadow-2xl"
      >
        <div className="bg-tb-navy text-tb-gold-bright px-4 py-3 flex items-center justify-between">
          <span className="font-hand text-xl">book your stay</span>
          <button
            data-testid={TIDS.close}
            type="button"
            onClick={onClose}
            className="font-hand text-tb-gold tb-tap inline-flex items-center gap-1"
          >
            <X className="w-4 h-4" /> close
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex items-center gap-3">
            {image && (
              <img
                src={image}
                alt=""
                className="w-16 h-16 rounded-xl object-cover border-2 border-tb-cream-3"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="font-serif-it text-tb-navy text-xl truncate">
                {name}
              </div>
              <div className="font-hand text-tb-navy/60 text-sm truncate">
                {d.subtitle || country}
              </div>
              {/* Price + savings */}
              <div className="flex items-center gap-2 mt-0.5">
                {priceUsd > 0 && (
                  <span className="font-serif text-tb-navy font-bold">
                    {currSymbol}{Math.round(priceUsd).toLocaleString()}
                  </span>
                )}
                {d.price_label && (
                  <span className="font-hand text-tb-navy/50 text-xs">{d.price_label}</span>
                )}
                {savings > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-ui font-bold">
                    Save ${savings}
                  </span>
                )}
              </div>
              {/* Cruise metadata */}
              {d.metadata?.cruise_line && (
                <span className="flex items-center gap-1 text-tb-navy/50 text-xs font-hand mt-0.5">
                  <Ship className="w-3 h-3" /> {d.metadata.cruise_line}
                  {d.metadata.duration_nights && ` · ${d.metadata.duration_nights} nights`}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="font-hand text-tb-navy/65 text-xs ml-1">start date</span>
              <input
                data-testid={TIDS.start}
                type="date"
                className={inp}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="font-hand text-tb-navy/65 text-xs ml-1">end date</span>
              <input
                data-testid={TIDS.end}
                type="date"
                className={inp}
                min={startDate || undefined}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </label>
          </div>

          {/* Inline no-availability banner */}
          {noAvailability && (
            <div
              className="rounded-xl border-2 border-tb-gold bg-tb-gold/10 text-tb-navy font-hand text-sm p-3 flex items-start gap-2"
            >
              <AlertTriangle className="w-4 h-4 text-tb-gold shrink-0 mt-0.5" />
              <span>No hotels available for these dates. Try different dates.</span>
            </div>
          )}

          {stay22Eligible && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-hand text-tb-navy/70 text-sm">
                  live availability
                </span>
                {stays.length > 0 && (
                  <span className="font-ui text-tb-navy/50 text-[10px]">
                    {stays.length} option{stays.length === 1 ? "" : "s"} · pick one to book
                  </span>
                )}
              </div>

              {staysLoading && (
                <div
                  data-testid={TIDS.staysLoading}
                  className="flex items-center gap-2 justify-center py-4 text-tb-navy/60 font-hand text-sm"
                >
                  <Loader2 className="w-4 h-4 animate-spin" /> checking rates…
                </div>
              )}

              {!staysLoading && staysError && (
                <div
                  data-testid={TIDS.staysError}
                  className="rounded-xl border-2 border-red-300 bg-red-50 text-red-700 font-hand text-xs p-3"
                >
                  {staysError}
                </div>
              )}

              {!staysLoading && !staysError && stays.length === 0 && (
                <div
                  data-testid={TIDS.staysEmpty}
                  className="rounded-xl border-2 border-tb-cream-3 bg-tb-cream-2 text-tb-navy/60 font-hand text-xs p-3 text-center"
                >
                  No live availability found for these dates — try broader dates or check back later.
                </div>
              )}

              {!staysLoading && stays.length > 0 && (
                <ul
                  data-testid={TIDS.staysList}
                  className="max-h-56 overflow-y-auto rounded-xl border-2 border-tb-cream-3 divide-y divide-tb-cream-3"
                >
                  {stays.map((s) => {
                    const suppliers = Object.keys(s.suppliers || {});
                    return (
                      <li key={s.stay22_property_id || s.booking_url}>
                        <button
                          type="button"
                          data-testid={`${TIDS.stayItem}-${s.stay22_property_id || "x"}`}
                          disabled={busy}
                          onClick={() => bookStay(s)}
                          className="w-full text-left p-2 flex gap-3 items-start transition bg-tb-cream hover:bg-tb-cream-2 border-l-4 border-transparent hover:border-tb-gold disabled:opacity-50"
                        >
                          {s.thumbnail_url ? (
                            <img
                              src={s.thumbnail_url}
                              alt=""
                              className="w-14 h-14 rounded-lg object-cover border border-tb-cream-3 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-14 h-14 rounded-lg bg-tb-cream-3 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-serif text-tb-navy text-sm font-bold truncate">
                              {s.name}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              {s.rating?.value > 0 && (
                                <span className="inline-flex items-center gap-0.5 text-tb-navy/70 text-xs font-ui">
                                  <Star className="w-3 h-3 fill-tb-gold text-tb-gold" />
                                  {s.rating.value}
                                  {s.rating.count > 0 && (
                                    <span className="text-tb-navy/40">({s.rating.count})</span>
                                  )}
                                </span>
                              )}
                              {suppliers.length > 0 && (
                                <span className="text-tb-navy/50 text-[10px] font-ui uppercase tracking-wide">
                                  via {suppliers.join(" · ")}
                                </span>
                              )}
                            </div>
                            {s.address && (
                              <div className="flex items-center gap-1 text-tb-navy/50 text-[11px] font-hand truncate mt-0.5">
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{s.address}</span>
                              </div>
                            )}
                          </div>
                          <ExternalLink className="w-4 h-4 text-tb-navy/30 shrink-0 mt-1" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {/* Legacy (non-Stay22) cards: single Book CTA */}
          {!stay22Eligible && (
            <button
              type="button"
              data-testid="book-now-legacy-cta"
              disabled={busy || !affiliateUrl}
              onClick={() => bookStay(null)}
              className="w-full py-3 rounded-xl bg-tb-navy text-tb-gold-bright font-hand text-lg border-2 border-tb-gold tb-tap disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> opening…</>
              ) : !affiliateUrl ? (
                // Attraction / non-bookable card — clarify why the button is greyed
                <>Booking not available for this card</>
              ) : (
                <><ExternalLink className="w-4 h-4" /> Book {name}</>
              )}
            </button>
          )}

          <p className="font-hand text-tb-navy/45 italic text-[10px] leading-tight text-center">
            opens the booking site in a new tab &middot; travelbio may
            receive a commission <ExternalLink className="w-3 h-3 inline -mt-0.5" />
          </p>
        </div>
      </motion.div>
    </div>
  );
}
